import { useCallback, useMemo } from 'react';

import { Link, type UIMatch, useLocation, useMatches, useParams, useSearchParams } from 'react-router';

import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, Flex } from '@chakra-ui/react';

import { get, isDefined, isNotDefined } from '@enterprise/core';
import type { RouteHandle } from '@enterprise/types';
import { ChevronRight, PortalOutlet } from '@enterprise/ui';

import { BREADCRUMB_PORTAL_ID } from '@/common/constants/breadcrumb-portal-id';

/**
 * Breadcrumbs component renders a navigational breadcrumb trail for the application.
 *
 * It uses React Router's `useMatches` and `useParams` to determine the current route hierarchy,
 * and Chakra UI's Breadcrumb components for styling.
 *
 * The breadcrumb items are dynamically generated based on the matched routes and their
 * associated breadcrumb metadata, supporting custom labels and dynamic property resolution
 * from route data or params.
 *
 * @returns {JSX.Element} The rendered breadcrumb navigation component.
 */
const Breadcrumbs = () => {
  const location = useLocation();
  const matches = useMatches() as UIMatch<Record<PropertyKey, string>, RouteHandle | undefined>[];
  const params = useParams();
  const [searchParams] = useSearchParams();

  const generateMetaData = useCallback(
    (match: UIMatch<Record<PropertyKey, string>, RouteHandle | undefined>, fallback: string) => {
      const { handle, loaderData, pathname } = match;

      let breadcrumb = handle?.breadcrumb;

      if (isNotDefined(breadcrumb)) {
        breadcrumb = { canNavigate: true, label: fallback };
      }

      const { canNavigate = true, label = fallback, propertyLocation, propertyName } = breadcrumb;

      if (isDefined(propertyLocation)) {
        if (isNotDefined(propertyName)) {
          return { canNavigate, label };
        }

        let providedLabel: string | undefined;

        switch (propertyLocation) {
          case 'data':
            if (isDefined(loaderData)) {
              providedLabel = get(loaderData, propertyName, label);
            }
            break;

          case 'param':
            providedLabel = get(params, propertyName, label);
            break;

          case 'searchParams':
            providedLabel = get(Object.fromEntries(searchParams), propertyName, label);
            break;

          case 'state':
            if (location.pathname === pathname) {
              providedLabel = get(location.state as Record<string, string | undefined>, propertyName, label);
            }
            break;
        }

        return { canNavigate, label: providedLabel ?? label };
      }

      return { canNavigate, label };
    },
    [params, location, searchParams]
  );

  const breadcrumbs = useMemo(() => {
    return matches.filter(({ handle }) => {
      return isDefined(handle?.breadcrumb);
    });
  }, [matches]);

  return (
    <Flex
      alignItems="center"
      bg="gray.50"
      mx={7}
      py={4}>
      <Breadcrumb
        alignItems="center"
        flex="1 0 0"
        flexDirection="row"
        fontSize="lg"
        separator={
          <ChevronRight
            className="text-hsp-500"
            fixedWidth
          />
        }
        spacing="3px">
        <BreadcrumbItem>
          <BreadcrumbLink
            as={Link}
            color="hsp.500"
            textDecoration="none"
            to="datastream"
            whiteSpace="nowrap">
            DataStream
          </BreadcrumbLink>
        </BreadcrumbItem>
        {breadcrumbs.map((breadcrumb, index) => {
          const { canNavigate, label } = generateMetaData(breadcrumb, breadcrumb.pathname);
          const isCurrentPage = breadcrumbs.length === 1 || index === breadcrumbs.length - 1;

          return (
            <BreadcrumbItem key={breadcrumb.id}>
              {canNavigate && (
                <BreadcrumbLink
                  as={Link}
                  color="hsp.500"
                  fontWeight="medium"
                  isCurrentPage={isCurrentPage}
                  textDecoration="none"
                  to={{ pathname: breadcrumb.pathname, search: searchParams.toString() }}
                  whiteSpace="nowrap">
                  {label}
                </BreadcrumbLink>
              )}
              {!canNavigate && (
                <BreadcrumbLink
                  as="div"
                  color="black"
                  fontWeight="medium"
                  isCurrentPage={isCurrentPage}
                  textDecoration="none"
                  whiteSpace="nowrap">
                  {label}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          );
        })}
      </Breadcrumb>
      <PortalOutlet id={BREADCRUMB_PORTAL_ID} />
    </Flex>
  );
};

export default Breadcrumbs;
